/**
 * The single shared AudioContext.
 *
 * Mobile browsers are miserly with audio contexts and will not start one
 * outside a user gesture, so there is exactly one, created lazily and resumed
 * on the first tap. Tone generation and microphone capture share it, which is
 * also what makes beat mode honest: the reference tone and the pipe meet in the
 * air of the room, not in a mixer.
 */

let context: AudioContext | null = null;

export function audioContext(): AudioContext {
  if (!context) {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) throw new Error("Web Audio is not available in this browser.");
    context = new Ctor({ latencyHint: "interactive" });
  }
  return context;
}

/** Resume the context. Must be called from within a user gesture on mobile. */
export async function resumeAudio(): Promise<void> {
  const ctx = audioContext();
  if (ctx.state === "suspended") await ctx.resume();
}

/**
 * `getUserMedia` is only exposed in a secure context, so plain HTTP over the
 * LAN silently has no microphone at all. Worth detecting up front and saying so
 * plainly, rather than letting the user wonder why the meter never moves.
 */
export function micAvailability(): { ok: true } | { ok: false; reason: string } {
  if (!window.isSecureContext) {
    return {
      ok: false,
      reason:
        "The microphone needs a secure context. Open this page over HTTPS (or on localhost) — plain http:// over the network will not work.",
    };
  }
  if (!navigator.mediaDevices?.getUserMedia) {
    return { ok: false, reason: "This browser does not expose microphone capture." };
  }
  return { ok: true };
}
